export type StoredAuthTokens = {
  email?: string;
  accessToken?: string;
  idToken?: string;
  expiresIn?: number;
  tokenType?: string;
};

type AuthTokenListener = (tokens: StoredAuthTokens | null) => void;

let currentTokens: StoredAuthTokens | null = null;
const listeners = new Set<AuthTokenListener>();

// Non-sensitive localStorage hint: tells us whether a refresh cookie might exist so we can
// skip the /auth/refresh call (and its 401) entirely for first-time unauthenticated visitors.
const SESSION_HINT_KEY = 'lovibook_sh';

export function hasSessionHint(): boolean {
  try { return localStorage.getItem(SESSION_HINT_KEY) === '1'; } catch { return false; }
}

function setSessionHint(): void {
  try { localStorage.setItem(SESSION_HINT_KEY, '1'); } catch { /* ignore */ }
}

function clearSessionHint(): void {
  try { localStorage.removeItem(SESSION_HINT_KEY); } catch { /* ignore */ }
}

export function getStoredAuthTokens(): StoredAuthTokens | null {
  return currentTokens;
}

export function setStoredAuthTokens(tokens: StoredAuthTokens | null) {
  currentTokens = tokens;
  if (tokens?.idToken) setSessionHint(); // successful auth → hint present for next load
  listeners.forEach((listener) => listener(currentTokens));
}

export function clearStoredAuthTokens() {
  clearSessionHint(); // logout or 401 → no refresh attempt on next page load
  setStoredAuthTokens(null);
}

export function isJwtExpired(token: string, nowMs = Date.now()): boolean {
  try {
    const encodedPayload = token.split('.')[1];
    if (!encodedPayload) {
      return false;
    }

    const normalizedPayload = encodedPayload
      .replace(/-/g, '+')
      .replace(/_/g, '/')
      .padEnd(Math.ceil(encodedPayload.length / 4) * 4, '=');
    const payload = JSON.parse(atob(normalizedPayload)) as { exp?: unknown };
    return typeof payload.exp === 'number' && payload.exp * 1000 <= nowMs + 30_000;
  } catch {
    return false;
  }
}

export function subscribeToStoredAuthTokens(listener: AuthTokenListener) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
